/**
 * Zielvorschau fuer Skills (Masterprompt Abschnitte 40, 52).
 *
 * Rueckmeldung aus dem Spiel (24.09.2026): Man sieht vor dem Wirken weder, wie
 * weit ein Skill reicht, noch welche Flaeche er trifft. Beides ist hier
 * ausgerechnet - als reine Funktion, damit es sich pruefen laesst, ohne ein
 * 3D-Bild aufzubauen.
 *
 * Wichtig: Diese Vorschau rechnet mit denselben Werten wie der Server
 * (Reichweite, Radius, Kegelwinkel). Sie ist Anzeige, keine Regel - aber sie
 * darf nie etwas anderes zeigen, als der Server spaeter trifft.
 */

import type { SkillDefinition } from '@dueline/game-data';
import type { Vec2 } from '@dueline/shared';

/** Form, die der Skill am Boden trifft. */
export type AimShape =
  | { kind: 'none' }
  /** Kreis um den Zielpunkt (Ground AoE). */
  | { kind: 'circle'; center: Vec2; radius: number }
  /** Kreisausschnitt vom Hero aus (Cone). */
  | { kind: 'cone'; origin: Vec2; direction: number; range: number; angleDegrees: number }
  /** Bahn vom Hero zum Zielpunkt (Skillshot, auch der Dash). */
  | { kind: 'bar'; origin: Vec2; target: Vec2; halfWidth: number };

export interface AimPreview {
  /** Mittelpunkt des Reichweitenrings - immer der Hero. */
  origin: Vec2;
  /** Groesste Entfernung, auf die dieser Skill wirkt. */
  range: number;
  /** Zielpunkt, bereits auf die Reichweite gekuerzt. */
  target: Vec2;
  shape: AimShape;
}

/**
 * Muss man fuer diesen Skill ueberhaupt zielen?
 *
 * Nein bei allem, was NUR am Hero selbst passiert (Rueckmeldung vom
 * 25.09.2026):
 * - Skills auf sich selbst (Smoke Bomb, Shadow Rift, Last Bastion),
 * - ein voller Kreis um den Hero (Blade Whirl).
 *
 * Solche Skills werden sofort gewirkt, sobald man den Knopf drueckt. Erst eine
 * Vorschau zu zeigen und einen zweiten Klick zu verlangen, kostet nur Zeit -
 * es gibt ja nichts auszurichten.
 */
export function needsAim(definition: SkillDefinition): boolean {
  if (definition.targeting === 'self') return false;
  if (definition.targeting === 'cone' && definition.coneAngleDegrees >= 360) return false;
  return true;
}

/**
 * Kuerzt einen Zielpunkt auf die Reichweite.
 *
 * Der Server macht dasselbe (`resolveAimPoint`). Ohne diese Kuerzung zeigte die
 * Vorschau einen Punkt an, den der Skill gar nicht erreicht - genau der
 * Fehlerfall, den sie verhindern soll.
 */
export function clampAim(origin: Vec2, target: Vec2, range: number): Vec2 {
  const dx = target.x - origin.x;
  const dy = target.y - origin.y;
  const entfernung = Math.hypot(dx, dy);
  if (entfernung <= range || entfernung === 0) return { x: target.x, y: target.y };

  const anteil = range / entfernung;
  return { x: origin.x + dx * anteil, y: origin.y + dy * anteil };
}

/**
 * Baut die Vorschau fuer einen Skill.
 *
 * @param origin Position des Heroes.
 * @param aim    Punkt, auf den der Finger gerade zeigt - oder null.
 * @param facing Blickrichtung, falls kein Punkt vorliegt.
 */
export function buildAimPreview(
  definition: SkillDefinition,
  origin: Vec2,
  aim: Vec2 | null,
  facing: number,
): AimPreview | null {
  // Ein Selbstbuff hat kein Ziel und keine Flaeche - da gibt es nichts zu zeigen.
  if (definition.targeting === 'self') return null;

  // Beim Dash zaehlt die Strecke, die der Hero zuruecklegt.
  const range = Math.max(definition.castRange, definition.dashDistance);
  const roh = aim ?? {
    x: origin.x + Math.cos(facing) * range,
    y: origin.y + Math.sin(facing) * range,
  };
  const target = clampAim(origin, roh, range);

  return { origin, range, target, shape: buildShape(definition, origin, target, range) };
}

function buildShape(
  definition: SkillDefinition,
  origin: Vec2,
  target: Vec2,
  range: number,
): AimShape {
  switch (definition.targeting) {
    case 'ground_aoe':
      return { kind: 'circle', center: target, radius: definition.radius };

    case 'cone':
      return {
        kind: 'cone',
        origin,
        direction: Math.atan2(target.y - origin.y, target.x - origin.x),
        range,
        angleDegrees: definition.coneAngleDegrees,
      };

    case 'skillshot':
      return { kind: 'bar', origin, target, halfWidth: definition.radius };

    default:
      return { kind: 'none' };
  }
}
